import { Link } from "react-router-dom";
import { MapPin, TrendingUp, DollarSign } from "lucide-react";
import { formatUSD } from "@/components/KPICard";
import { API } from "@/lib/api";
import { PROPERTIES } from "@/constants/testIds";

const COUNTRY_META = {
  AR: { label: "AR", currency: "ARS", dot: "bg-sky-400", tone: "text-sky-500 border-sky-500/30" },
  US: { label: "US", currency: "USD", dot: "bg-emerald-400", tone: "text-emerald-500 border-emerald-500/30" },
  MX: { label: "MX", currency: "MXN", dot: "bg-amber-400", tone: "text-amber-500 border-amber-500/30" },
};

const STATUS_META = {
  occupied: { label: "Ocupada", tone: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" },
  vacant: { label: "Vacía", tone: "bg-rose-500/10 text-rose-500 border-rose-500/20" },
  maintenance: { label: "Mantenimiento", tone: "bg-amber-500/10 text-amber-500 border-amber-500/20" },
};

export default function PropertyCard({ property, roi, netUsd, valueUsd }) {
  const meta = COUNTRY_META[property.country] || { label: property.country, tone: "text-muted-foreground border-border" };
  const st = STATUS_META[property.status] || { label: property.status, tone: "bg-secondary text-muted-foreground border-border" };
  return (
    <Link
      to={`/properties/${property.id}`}
      data-testid={`${PROPERTIES.card}-${property.id}`}
      className="group relative flex flex-col rounded-xl overflow-hidden border border-border/60 bg-card transition-transform duration-200 hover:-translate-y-1 hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring animate-fade-in-up"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-secondary">
        {(property.photo_gridfs_id || property.photo_url) ? (
          <img
            src={property.photo_gridfs_id ? `${API}/properties/${property.id}/photo?v=${property.photo_gridfs_id}` : property.photo_url}
            alt={property.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-muted-foreground">Sin foto</div>
        )}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className={`country-badge bg-background/70 backdrop-blur-md ${meta.tone}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dot || "bg-current"}`} />
            {meta.label}
          </span>
          <span className={`country-badge ${st.tone}`}>{st.label}</span>
        </div>
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-heading font-semibold text-lg tracking-tight line-clamp-1">{property.name}</h3>
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin className="w-3.5 h-3.5" />
          <span className="line-clamp-1">{property.city || property.address}</span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Valor</div>
            <div className="text-sm font-semibold num">{formatUSD(valueUsd)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Neto</div>
            <div className={`text-sm font-semibold num ${netUsd >= 0 ? "text-emerald-500" : "text-rose-500"}`}>
              {formatUSD(netUsd)}
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-border/60 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <TrendingUp className="w-3.5 h-3.5" /> Rentabilidad
          </div>
          <div className={`text-sm font-heading font-semibold num ${roi >= 0 ? "text-emerald-500" : "text-rose-500"}`}>
            {roi?.toFixed(1)}%
          </div>
        </div>
      </div>
    </Link>
  );
}
